import { type HttpClient } from '@packages/core/http';

import type {
    GetAllDtcsLine,
    GetAllDtcsRequest,
    GetAllDtcsResponse,
    GetConnectionStatusRequest,
    GetConnectionStatusResponse,
    GetDiagramRequest,
    GetDiagramResponse,
    GetDiagramSvgRequest,
    GetDiagramSvgResponse,
    GetFreezeFramesDocumentRequest,
    GetFreezeFramesDocumentResponse,
    GetFreezeFramesRequest,
    GetFreezeFramesResponse,
    GetLastConnectionResponse,
    GetRecentConnectionByVinRequest,
    GetRecentConnectionByVinResponse,
    GetVehicleByCloudConnectorIdLine,
    GetVehicleByCloudConnectorIdRequest,
    GetVehicleByCloudConnectorIdResponse,
} from './dtos';

/** Receives every line of a DTC read as it is streamed, the last one included. */
export type DtcReadLineListener = (line: GetAllDtcsLine) => void;

/** Receives every line of a vehicle lookup as it is streamed, the last one included. */
export type VehicleLookupLineListener = (line: GetVehicleByCloudConnectorIdLine) => void;

/**
 * API client for the SSM5 backend. Its endpoints are not deployed yet, so the container serves the
 * scripted client in its place until they are; the DTOs already mirror the agreed contract.
 */
export class Ssm5Client {
    /** @param http - the HTTP client */
    constructor(protected http: HttpClient) {}

    /**
     * Read every DTC of the vehicle behind the Cloud Connector of an open session.
     *
     * Endpoint: GET /public-diag/cc/{cc_id}/vehicle/units/dtc, answered as a JSON Lines stream.
     *
     * @param request - the Cloud Connector ID
     * @param _signal - aborts the read
     * @param _onLine - receives every streamed line
     * @returns the last streamed line; rejects when aborted
     */
    getAllDtcs(
        request: GetAllDtcsRequest,
        _signal?: AbortSignal,
        _onLine?: DtcReadLineListener
    ): Promise<GetAllDtcsResponse> {
        return Promise.reject(
            new Error(`The SSM5 DTC read is not wired to a backend yet (${request.cloudConnectorId})`)
        );
    }

    /**
     * Check whether the connection behind a Cloud Connector is still open, so a session the
     * technician left can be told apart from one that has since expired or been lost.
     *
     * No endpoint exists for this check yet.
     *
     * @param request - the Cloud Connector ID
     * @param _signal - aborts the check
     * @returns whether the connection is still open; rejects when aborted
     */
    getConnectionStatus(
        request: GetConnectionStatusRequest,
        _signal?: AbortSignal
    ): Promise<GetConnectionStatusResponse> {
        return Promise.reject(
            new Error(`The SSM5 connection status check is not wired to a backend yet (${request.cloudConnectorId})`)
        );
    }

    /**
     * Read the CAN Bus diagram of the vehicle behind the Cloud Connector of an open session: where the
     * file of its model and model year is, and the status of every module on it.
     *
     * Endpoint: GET /ssm5/cloud-connectors/{cloudConnectorId}.
     *
     * @param request - the Cloud Connector ID
     * @param _signal - aborts the read
     * @returns the file link and the modules; rejects when aborted
     */
    getDiagram(request: GetDiagramRequest, _signal?: AbortSignal): Promise<GetDiagramResponse> {
        return Promise.reject(
            new Error(`The SSM5 diagram read is not wired to a backend yet (${request.cloudConnectorId})`)
        );
    }

    /**
     * Read the diagram file the diagram read points at. The link is a presigned one outside the backend,
     * so the file is fetched as is: the HTTP client would prefix the backend address, send the token and
     * cache the answer. This read works before the backend is wired, since the link decides where it goes.
     *
     * @param request - the file link
     * @param signal - aborts the read
     * @returns the SVG text; rejects when aborted or when the file cannot be read
     */
    async getDiagramSvg({ svgUrl }: GetDiagramSvgRequest, signal?: AbortSignal): Promise<GetDiagramSvgResponse> {
        const response = await fetch(svgUrl, { signal });

        if (!response.ok) {
            throw new Error(`The SSM5 diagram file could not be read (${response.status})`);
        }

        return response.text();
    }

    /**
     * Read the freeze frame data of a DTC behind the Cloud Connector of an open session.
     *
     * Endpoint: GET /ssm5/cloud-connectors/{cloudConnectorId}/freeze-frames.
     *
     * @param request - the Cloud Connector ID, module and trouble code
     * @param _signal - aborts the read
     * @returns the readout or a named contract error; rejects when aborted
     */
    getFreezeFrames(request: GetFreezeFramesRequest, _signal?: AbortSignal): Promise<GetFreezeFramesResponse> {
        return Promise.reject(
            new Error(`The SSM5 freeze frame read is not wired to a backend yet (${request.cloudConnectorId})`)
        );
    }

    /**
     * Download the freeze frame document of a DTC through the Cloud Connector of an open session.
     *
     * Endpoint: GET /ssm5/cloud-connectors/{cloudConnectorId}/freeze-frames/document.
     *
     * @param request - the session, DTC, document format and optional PID name fragment
     * @param _signal - aborts the download
     * @returns the document file; rejects when aborted or when the download fails
     */
    getFreezeFramesDocument(
        request: GetFreezeFramesDocumentRequest,
        _signal?: AbortSignal
    ): Promise<GetFreezeFramesDocumentResponse> {
        return Promise.reject(
            new Error(
                `The SSM5 freeze frame document download is not wired to a backend yet (${request.cloudConnectorId})`
            )
        );
    }

    /**
     * Look up the technician's most recent eligible SSM5 connection.
     *
     * No endpoint exists for this lookup yet.
     *
     * @param _signal - aborts the lookup
     * @returns the last connection, or undefined when the technician has none; rejects when aborted
     */
    getLastConnection(_signal?: AbortSignal): Promise<GetLastConnectionResponse> {
        return Promise.reject(new Error('The SSM5 last-connection lookup is not wired to a backend yet'));
    }

    /**
     * Look up the most recent eligible SSM5 connection of a VIN.
     *
     * No endpoint exists for this lookup yet.
     *
     * @param request - the VIN
     * @param _signal - aborts the lookup
     * @returns the most recent connection, or undefined when the VIN has none; rejects when aborted
     */
    getRecentConnectionByVin(
        request: GetRecentConnectionByVinRequest,
        _signal?: AbortSignal
    ): Promise<GetRecentConnectionByVinResponse> {
        return Promise.reject(
            new Error(`The SSM5 recent-connection lookup is not wired to a backend yet (${request.vin})`)
        );
    }

    /**
     * Look a vehicle up through its Cloud Connector.
     *
     * Endpoint: GET /public/cc/{cc_id}/vehicle, answered as a JSON Lines stream.
     *
     * @param request - the Cloud Connector ID
     * @param _signal - aborts the lookup
     * @param _onLine - receives every streamed line
     * @returns the last streamed line; rejects when aborted
     */
    getVehicleByCloudConnectorId(
        request: GetVehicleByCloudConnectorIdRequest,
        _signal?: AbortSignal,
        _onLine?: VehicleLookupLineListener
    ): Promise<GetVehicleByCloudConnectorIdResponse> {
        return Promise.reject(
            new Error(`The SSM5 vehicle lookup is not wired to a backend yet (${request.cloudConnectorId})`)
        );
    }
}
